// What's-new banner. Compares the API's current git SHA against a
// cookie-stored SHA from the last visit; if they differ, shows a small
// one-line "Updates landed" banner with a link. Cookie updates only
// when the user dismisses (or visits the changelog), so a user who
// ignores the banner keeps seeing it until they engage.
//
// Round-3 fix #19 (Diane, Jennifer): users have no signal that the app
// has changed between visits.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { env } from '../env';

const COOKIE = 'medreview_seen_sha';

function readCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE}=([^;]+)`));
  return m ? decodeURIComponent(m[1] ?? '') : null;
}

function writeCookie(sha: string): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${COOKIE}=${encodeURIComponent(sha)}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
}

export function WhatsNewBanner(): JSX.Element | null {
  const [currentSha, setCurrentSha] = useState<string | null>(null);
  const [seenSha, setSeenSha] = useState<string | null>(readCookie);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${env.apiBaseUrl}/api/health`);
        if (!res.ok) return;
        const j = (await res.json()) as { gitSha?: string };
        if (cancelled) return;
        if (j.gitSha && j.gitSha !== 'unknown') setCurrentSha(j.gitSha);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!currentSha || dismissed) return null;
  // First visit ever: silently record the current SHA and stay hidden.
  if (!seenSha) {
    writeCookie(currentSha);
    setSeenSha(currentSha);
    return null;
  }
  if (seenSha === currentSha) return null;

  const acknowledge = (): void => {
    writeCookie(currentSha);
    setSeenSha(currentSha);
    setDismissed(true);
  };

  return (
    <div
      style={{
        margin: '0 0 var(--space-md)',
        padding: '10px 14px',
        background: 'var(--color-surface-secondary)',
        border: '1.5px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        fontSize: 15,
      }}
    >
      <span>
        Updates landed since your last visit.{' '}
        <Link to="/methodology" onClick={acknowledge} className="account-link">
          See what changed
        </Link>
        .
      </span>
      <button
        type="button"
        onClick={acknowledge}
        aria-label="Dismiss what's-new banner"
        style={{
          background: 'transparent',
          border: 0,
          cursor: 'pointer',
          fontSize: 18,
          color: 'var(--color-text-secondary)',
          padding: '0 4px',
        }}
      >
        ×
      </button>
    </div>
  );
}
